import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { failureText } from "../lib/reports.js";

/**
 * Who a report or dashboard is shared with: people by their sign-in name and groups.
 *
 * Saying so shares the *question*. Whoever opens it is answered for what they
 * may open, which is said here because it is the thing a person sharing a chart
 * wants to be sure of before they press the button. Unsharing is the same
 * dialog with fewer names, and takes effect for the next thing they open.
 *
 * Names are typed, and the kernel says which one it has never heard of. A group
 * the person is in is offered as a quick pick; any other group is typed.
 */
export default function ShareDialog({ item, myGroups = [], onClose, onShare }) {
  const [users, setUsers] = useState(item?.sharedUsers || []);
  const [groups, setGroups] = useState(item?.sharedGroups || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await onShare(item, users, groups);
      onClose();
    } catch (e) {
      setError(failureText(e));
    } finally {
      setBusy(false);
    }
  };

  const chips = (values, getTagProps) => values.map((value, index) => {
    const { key, ...props } = getTagProps({ index });
    return <Chip key={key} size="small" label={value} {...props} />;
  });

  return (
    <Dialog open={Boolean(item)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="share-title">
      <DialogTitle id="share-title" sx={{ overflowWrap: "anywhere" }}>Share “{item?.name}”</DialogTitle>
      <DialogContent dividers>
        <Stack sx={{ gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Sharing gives them the question, not the answer. When they open it they are answered for the
            records <em>they</em> can open, so their figures can differ from yours.
          </Typography>
          <Autocomplete
            multiple freeSolo options={[]} value={users} onChange={(_, v) => setUsers(v.map((x) => String(x).trim()).filter(Boolean))}
            renderValue={chips}
            renderInput={(params) => (
              <TextField {...params} label="People" size="small"
                helperText="Their sign-in name, such as ana@company.com. Press Enter after each." />
            )}
          />
          <Autocomplete
            multiple freeSolo options={myGroups} value={groups} onChange={(_, v) => setGroups(v.map((x) => String(x).trim()).filter(Boolean))}
            renderValue={chips}
            renderInput={(params) => (
              <TextField {...params} label="Groups" size="small"
                helperText="Everyone in a group can open it. Your own groups are offered." />
            )}
          />
          {error ? <Alert severity="warning" role="alert">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save sharing"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
